// Per-avatar presentation hints stored alongside the clip URLs in the avatar's
// `animations` JSON. That JSON is what the local store holds and what every
// client broadcasts as its animUrls, so a hint set there reaches remote viewers
// with no extra plumbing.
//
//   __height  standing height in metres (bind pose, feet to top of head). The
//             renderer scales the model to it and places the name tag and the
//             first-person eye from it. Absent: the model's own size and the
//             human defaults (tag 1.92 m, eye 1.6 m).
//   __hover   "true" for hover bots: a gentle bob a few cm off the floor and
//             damped leg swing while walking.
//   __format  "vrm" for uploaded VRM avatars (src/avatar/vrm). The model URL
//             also ends in .vrm; either one makes clients load it as a VRM
//             (spring bones, blink, lip sync). __height is the VRM's own height.

export interface AvatarMeta {
  height: number | null;
  hover: boolean;
  format: 'vrm' | 'gltf';
}

export function readAvatarMeta(anims: Record<string, unknown> | null | undefined): AvatarMeta {
  const h = Number(anims?.__height);
  const hover = String(anims?.__hover ?? '').toLowerCase();
  return {
    // Sanity bounds so a bad value can't make an avatar vanish or fill a room.
    height: Number.isFinite(h) && h >= 0.4 && h <= 3 ? h : null,
    hover: hover === 'true' || hover === '1',
    format: String(anims?.__format ?? '').toLowerCase() === 'vrm' ? 'vrm' : 'gltf',
  };
}

/** Name-tag height above the feet for an avatar this tall (human default 1.92 m). */
export const nameTagHeight = (height: number | null) => (height ? height + 0.28 : 1.92);
/** First-person eye height above the feet (human default 1.6 m). */
export const eyeHeight = (height: number | null) => (height ? height * 0.86 : 1.6);
/** Third-person orbit look-at height above the feet (human default 1.5 m). */
export const lookHeight = (height: number | null) => (height ? Math.max(0.8, height * 0.88) : 1.5);

/** Hover tuning: rest lift, bob amplitude (m) and frequency (Hz). */
export const HOVER = { lift: 0.06, amp: 0.025, hz: 0.5, legDamp: 0.6 };
